import {
  CheckIcon,
  ChevronDownIcon,
  type LucideIcon,
  XCircleIcon,
  XIcon,
} from 'lucide-react'
import React, { useCallback } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandLoading,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/cn'

import type { Option } from '@/types/option'

type MultiSelectProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  options: Array<
    Option & {
      icon?: LucideIcon
      className?: string
      style?: React.CSSProperties
    }
  >
  onValueChange: (value: string[]) => void

  defaultValues?: string[]
  placeholder?: string
  maxItemsToDisplay?: number
  modalPopover?: boolean

  /**
   * Busca controlada pelo consumidor (busca server-side).
   *
   * Passar `handleSearch` transfere a filtragem para quem renderiza: a lista
   * exibe `options` como recebida, sem o filtro client-side do `cmdk`. Sem
   * `handleSearch` nada muda — o campo segue sendo interno e filtrando local,
   * que é como todos os consumidores de filtro usam o componente hoje.
   */
  search?: string
  handleSearch?: (search: string) => void
  searchPlaceholder?: string
  emptyMessage?: string
  /** Substitui a lista por skeletons enquanto a busca está em voo. */
  loading?: boolean
}

const MultiSelect = React.forwardRef<HTMLButtonElement, MultiSelectProps>(
  (
    {
      options,
      onValueChange,
      defaultValues = [],
      placeholder = 'Selecione as opções',
      maxItemsToDisplay = 3,
      modalPopover = false,
      search,
      handleSearch,
      searchPlaceholder = 'Buscar...',
      emptyMessage = 'Nenhum resultado encontrado.',
      loading = false,
      className,
      ...props
    },
    ref,
  ) => {
    const isSearchControlled = handleSearch !== undefined
    const [selectedValues, setSelectedValues] =
      React.useState<string[]>(defaultValues)
    const [isPopoverOpen, setIsPopoverOpen] = React.useState(false)

    const handleInputKeyDown = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
          setIsPopoverOpen(true)
          return
        }

        if (event.key === 'Backspace' && !event.currentTarget.value) {
          const newSelectedValues = [...selectedValues]
          newSelectedValues.pop()
          setSelectedValues(newSelectedValues)
          onValueChange(newSelectedValues)
        }
      },
      [onValueChange, selectedValues],
    )

    const toggleOption = useCallback(
      (option: string) => {
        const newSelectedValues = selectedValues.includes(option)
          ? selectedValues.filter((value) => value !== option)
          : [...selectedValues, option]
        setSelectedValues(newSelectedValues)
        onValueChange(newSelectedValues)
      },
      [onValueChange, selectedValues],
    )

    const handleClear = useCallback(() => {
      setSelectedValues([])
      onValueChange([])
    }, [onValueChange])

    const handleTogglePopover = useCallback(() => {
      setIsPopoverOpen((prev) => !prev)
    }, [])

    const clearExtraOptions = useCallback(() => {
      const newSelectedValues = selectedValues.slice(0, maxItemsToDisplay)
      setSelectedValues(newSelectedValues)
      onValueChange(newSelectedValues)
    }, [maxItemsToDisplay, onValueChange, selectedValues])

    return (
      <Popover
        open={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        modal={modalPopover}
      >
        <PopoverTrigger asChild>
          <Button
            ref={ref}
            onClick={handleTogglePopover}
            className={cn(
              'flex w-full px-2 py-3 rounded-md border border-system-border-secondary min-h-10 h-auto items-center justify-between bg-white hover:bg-white',
              className,
            )}
            role="button"
            aria-label="multi-select"
            {...props}
          >
            {selectedValues.length > 0 ? (
              <div className="flex w-full items-start justify-between gap-2">
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2">
                  {selectedValues.slice(0, maxItemsToDisplay).map((value) => {
                    const option = options.find((o) => o.value === value)
                    const IconComponent = option?.icon

                    return (
                      <Badge
                        key={value}
                        className={cn(
                          'flex rounded bg-system-content-american-gray/10 text-xs font-medium text-system-content-american-gray transition delay-150 duration-300 ease-in-out hover:-translate-y-1 hover:scale-110 hover:bg-transparent gap-2 hover:border hover:border-system-border-primary max-w-48',
                          option?.className || '',
                        )}
                        style={option?.style}
                      >
                        {IconComponent && (
                          <IconComponent className="size-4 shrink-0" />
                        )}
                        <span className="block min-w-0 grow truncate">
                          {option?.label}
                        </span>
                        <XCircleIcon
                          className="size-4 shrink-0 cursor-pointer"
                          onClick={(event) => {
                            event.stopPropagation()
                            toggleOption(value)
                          }}
                        />
                      </Badge>
                    )
                  })}
                  {selectedValues.length > maxItemsToDisplay && (
                    <Badge
                      className={cn(
                        'flex rounded bg-system-content-american-gray/10 text-xs font-medium text-system-content-american-gray transition delay-150 duration-300 ease-in-out hover:-translate-y-1 hover:scale-110 hover:bg-transparent gap-2 hover:border hover:border-system-border-primary',
                      )}
                    >
                      {`+ ${selectedValues.length - maxItemsToDisplay} mais`}
                      <XCircleIcon
                        className="size-4 cursor-pointer"
                        onClick={(event) => {
                          event.stopPropagation()
                          clearExtraOptions()
                        }}
                      />
                    </Badge>
                  )}
                </div>
                <div className="flex shrink-0 items-center justify-between gap-2">
                  {selectedValues.length > maxItemsToDisplay && (
                    <>
                      <XIcon
                        role="button"
                        aria-label="clear"
                        className="h-4 cursor-pointer text-system-primary-icon"
                        onClick={(event) => {
                          event.stopPropagation()
                          handleClear()
                        }}
                      />
                      <Separator
                        orientation="vertical"
                        className="flex h-full min-h-6"
                      />
                    </>
                  )}
                  <ChevronDownIcon className="mx-2 h-4 cursor-pointer text-system-primary-icon" />
                </div>
              </div>
            ) : (
              <div className="mx-auto flex w-full items-center justify-between">
                <span className="mx-3 text-sm text-system-text-secondary">
                  {placeholder}
                </span>
                <ChevronDownIcon className="mx-2 h-4 cursor-pointer text-system-primary-icon" />
              </div>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-[--radix-popover-trigger-width] p-0"
          align="start"
          onEscapeKeyDown={() => setIsPopoverOpen(false)}
        >
          <Command shouldFilter={!isSearchControlled}>
            <CommandInput
              value={isSearchControlled ? search : undefined}
              onValueChange={handleSearch}
              placeholder={searchPlaceholder}
              onKeyDown={handleInputKeyDown}
            />
            <CommandList>
              {!loading && <CommandEmpty>{emptyMessage}</CommandEmpty>}

              {loading && (
                <CommandLoading>
                  <div className="flex flex-col gap-2">
                    <Skeleton className="h-9 w-full" />
                    <Skeleton className="h-9 w-full" />
                  </div>
                </CommandLoading>
              )}

              <CommandGroup>
                {!loading &&
                  options.map((option) => {
                    const isSelected = selectedValues.includes(option.value)
                    const IconComponent = option?.icon

                    return (
                      <CommandItem
                        key={option.value}
                        onSelect={() => toggleOption(option.value)}
                        className="flex cursor-pointer gap-2 text-system-text-primary"
                      >
                        <div
                          className={cn(
                            'flex h-4 w-4 items-center justify-center rounded-sm border border-system-border-primary',
                            isSelected
                              ? 'bg-system-primary-blue text-white'
                              : 'opacity-50 [&_svg]:invisible',
                          )}
                        >
                          <CheckIcon className="size-4" />
                        </div>
                        {IconComponent && <IconComponent className="size-4" />}
                        <span>{option.label}</span>
                      </CommandItem>
                    )
                  })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    )
  },
)
MultiSelect.displayName = 'MultiSelect'

export { MultiSelect }
